// IL COPIONE DEL TUTORIAL DEL POKER CINESE (I-1218; KB `services/qchinesepoker/tutorial-copione.md` @ dc39499c, §2 le mani, §3 i 12 passi, §4 l'innesto): una mano didattica FISSA contro il robot, sul tavolo vero.
// PURO: nessun React, nessuna rete, nessuno storage, nessun tempo. Il Motore e` A COPIONE (nel client non c'e` un valutatore: `useOfcPlacement.ts:23-25`, `resoconto.ts:3-18`): `applica` rende lo stato scritto
// dopo la mossa, `legale` e` la regola di CAPIENZA (carte della mano, righe con posto), non di forza. Le plance finali e il resoconto sono quelli MISURATI col motore vero (`@qcp/rules` @ 9e799e5, KB §2); un test li fissa.
// I passi chiedono il testo a `chinesepoker:guida.passi.<id>.{titolo,testo,altra}` (`tutorial.*` e` del foglio delle regole).
import type { Board, Card, HandSettledPayload, Row, Seat } from '@/lib/qcp/protocol';
// relativo e con `.ts`: lo legge anche il banco (node), che non conosce l'alias `@/`
import { AVANTI } from '../../lib/tutorial/tipi.ts';
import type { Copione, Motore, Passo } from '@/lib/tutorial/tipi';

// ── le carte, come le vede chi gioca ────────────────────────────────────────────────────────────────────────────
const RANGHI: Record<string, number> = { '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, J: 11, Q: 12, K: 13, A: 14 };
const SEMI: Record<string, Card['s']> = { '♠': 's', '♥': 'h', '♦': 'd', '♣': 'c' };

/** «K♠» → `{ r: 13, s: 's' }`. */
export const carta = (nome: string): Card => ({ r: RANGHI[nome.slice(0, -1)] as number, s: SEMI[nome.slice(-1)] as Card['s'] });
const carte = (nomi: string): Card[] => nomi.split(' ').map(carta);
export const chiave = (c: Card): string => `${c.r}${c.s}`;

export const CAPIENZA: Record<Row, number> = { front: 3, middle: 5, back: 5 };
export const FILE: readonly Row[] = ['front', 'middle', 'back'];
/** Le carte che chi impara DEVE piazzare in un giro: 5 all'apertura, poi 2 delle 3 (la terza e` lo scarto). */
export const QUOTA = (giro: number): number => (giro === 0 ? 5 : 2);

// ── §2: le mani (34 carte uniche) ───────────────────────────────────────────────────────────────────────────────
/** Le carte che arrivano a chi impara (posto A), giro per giro. */
export const ARRIVANO_IO: readonly (readonly Card[])[] = [carte('K♠ K♥ 9♦ 8♦ 3♣'), carte('Q♦ 7♦ 2♠'), carte('4♣ 4♥ 2♥'), carte('10♠ J♣ 2♣'), carte('A♦ 9♠ 5♥')];
/** Le carte del robot (posto B), giro per giro. */
export const ARRIVANO_ROBOT: readonly (readonly Card[])[] = [carte('Q♠ Q♣ 7♣ 7♠ 10♥'), carte('A♠ 8♣ K♦'), carte('6♥ 8♥ A♣'), carte('5♣ 2♦ 6♠'), carte('3♠ 6♣ J♥')];

export interface Piazzamento {
  carta: Card;
  fila: Row;
}
const piazza = (...p: [string, Row][]): Piazzamento[] => p.map(([c, fila]) => ({ carta: carta(c), fila }));

/** §2, colonna «dove (attese)»: i piazzamenti di chi impara. Lo scarto e` la carta che resta. */
const DOVE_IO: readonly (readonly Piazzamento[])[] = [
  piazza(['K♠', 'back'], ['K♥', 'back'], ['9♦', 'middle'], ['8♦', 'middle'], ['3♣', 'front']),
  piazza(['Q♦', 'middle'], ['7♦', 'middle']),
  piazza(['4♣', 'back'], ['4♥', 'back']),
  piazza(['10♠', 'back'], ['J♣', 'front']),
  piazza(['9♠', 'middle'], ['5♥', 'front']),
];
/** §2, colonna «il robot mette». */
const DOVE_ROBOT: readonly (readonly Piazzamento[])[] = [
  piazza(['Q♠', 'back'], ['Q♣', 'back'], ['7♣', 'middle'], ['7♠', 'middle'], ['10♥', 'front']),
  piazza(['A♠', 'middle'], ['8♣', 'back']),
  piazza(['6♥', 'back'], ['8♥', 'front']),
  piazza(['5♣', 'middle'], ['2♦', 'back']),
  piazza(['3♠', 'middle'], ['6♣', 'front']),
];
/** Gli scarti (coperti) giro per giro: chi impara, il robot. Il giro 0 non scarta. */
export const SCARTI_IO: readonly Card[] = carte('2♠ 2♥ 2♣ A♦');
export const SCARTI_ROBOT: readonly Card[] = carte('K♦ A♣ 6♠ J♥');

// ── lo stato, la mossa, il motore ───────────────────────────────────────────────────────────────────────────────
export interface StatoCp {
  /** Il giro (0-4) che chi impara sta giocando; passa al successivo quando il robot ha giocato il suo. */
  giro: number;
  io: Board;
  robot: Board;
  /** Le carte ancora in mano a chi impara (quelle da piazzare e, dopo i due piazzamenti, la terza). */
  mano: readonly Card[];
  /** Gli scarti coperti, per il conto sotto la plancia. */
  scarti: Record<Seat, number>;
  /** Il resoconto della mano, solo al passo del conto. */
  resoconto: HandSettledPayload | null;
}

/**
 * Una MOSSA: i piazzamenti di UN posto in UN giro. Quella di chi impara (posto A) arriva dal tavolo quando i piazzamenti raggiungono il numero che il passo aspetta; con `conferma`
 * chiude il giro (la terza carta resta come scarto). I giri 0 dei passi 3 e 4 sono mosse PARZIALI (un piazzamento, senza conferma): il KB le giudica una per una.
 * Quella del robot (posto B) e` l'intero giro, in `dopo`: la sua carta in piu` e` lo scarto.
 */
export interface MossaCp {
  posto: Seat;
  giro: number;
  piazza: readonly Piazzamento[];
  conferma?: boolean;
}

/** I bersagli del copione: cosa si illumina, e per «Mostramelo». */
export type BersaglioCp = { t: 'carta'; carta: Card } | { t: 'fila'; fila: Row } | { t: 'conferma' } | { t: 'totale' };

const plancia = (): Board => ({ front: [], middle: [], back: [] });
const conAggiunte = (b: Board, p: readonly Piazzamento[]): Board => ({
  front: [...b.front, ...p.filter((x) => x.fila === 'front').map((x) => x.carta)],
  middle: [...b.middle, ...p.filter((x) => x.fila === 'middle').map((x) => x.carta)],
  back: [...b.back, ...p.filter((x) => x.fila === 'back').map((x) => x.carta)],
});

export const STATO0: StatoCp = { giro: 0, io: plancia(), robot: plancia(), mano: ARRIVANO_IO[0] as Card[], scarti: { A: 0, B: 0 }, resoconto: null };

function applica(s: StatoCp, m: MossaCp): StatoCp {
  if (m.posto === 'A') {
    const usate = new Set(m.piazza.map((p) => chiave(p.carta)));
    const resta = s.mano.filter((c) => !usate.has(chiave(c)));
    // con la conferma la terza carta e` scartata (coperta): in mano non resta niente
    return { ...s, io: conAggiunte(s.io, m.piazza), mano: m.conferma === true ? [] : resta, scarti: { ...s.scarti, A: s.scarti.A + (m.conferma === true && m.giro > 0 ? 1 : 0) } };
  }
  // il robot gioca il suo giro; poi arriva il giro dopo a chi impara (transizione automatica: sta dentro `applica`, regola 2 del kit)
  const prossimo = s.giro + 1;
  return {
    ...s,
    robot: conAggiunte(s.robot, m.piazza),
    scarti: { ...s.scarti, B: s.scarti.B + (m.giro > 0 ? 1 : 0) },
    giro: prossimo < ARRIVANO_IO.length ? prossimo : s.giro,
    mano: prossimo < ARRIVANO_IO.length ? (ARRIVANO_IO[prossimo] as Card[]) : s.mano,
  };
}

function legale(s: StatoCp, m: MossaCp): boolean {
  // il tutorial muove solo chi impara: il robot e` `dopo`, che non passa dal giudizio
  if (m.posto !== 'A' || m.giro !== s.giro || s.resoconto !== null || m.piazza.length === 0) return false;
  const inMano = new Set(s.mano.map(chiave));
  const viste = new Set<string>();
  for (const p of m.piazza) {
    const k = chiave(p.carta);
    if (!inMano.has(k) || viste.has(k)) return false;
    viste.add(k);
  }
  for (const fila of FILE) {
    if (s.io[fila].length + m.piazza.filter((p) => p.fila === fila).length > CAPIENZA[fila]) return false;
  }
  // la conferma chiude il giro: i piazzamenti di questo giro (quelli di prima e questi) sono la quota
  if (m.conferma === true) {
    const arrivate = (ARRIVANO_IO[s.giro] as readonly Card[]).length;
    return arrivate - s.mano.length + m.piazza.length === QUOTA(s.giro);
  }
  return true;
}

const stessiPiazzamenti = (a: readonly Piazzamento[], b: readonly Piazzamento[]): boolean => {
  const k = (p: Piazzamento) => `${chiave(p.carta)}>${p.fila}`;
  const x = a.map(k).sort();
  const y = b.map(k).sort();
  return x.length === y.length && x.every((v, i) => v === y[i]);
};

/** `piazza` si confronta come INSIEME: l'ordine dei tocchi non conta. */
function uguale(a: MossaCp, b: MossaCp): boolean {
  return a.posto === b.posto && a.giro === b.giro && (a.conferma === true) === (b.conferma === true) && stessiPiazzamenti(a.piazza, b.piazza);
}

export const MOTORE_CP: Motore<StatoCp, StatoCp, MossaCp> = { vista: (s) => s, applica, legale, uguale };

// ── le mosse del copione ────────────────────────────────────────────────────────────────────────────────────────
const mossaIo = (giro: number, da: number, a: number, conferma: boolean): MossaCp => ({ posto: 'A', giro, piazza: (DOVE_IO[giro] as readonly Piazzamento[]).slice(da, a), ...(conferma ? { conferma } : {}) });
const mossaRobot = (giro: number): MossaCp => ({ posto: 'B', giro, piazza: DOVE_ROBOT[giro] as readonly Piazzamento[] });

// ── lo stato con cui parte un passo che salta ───────────────────────────────────────────────────────────────────
/** Gioca i giri `0..fino` (chi impara e poi il robot): lo stato all'inizio del giro `fino + 1`. Serve ai `salto` e ai test. */
function giocaFino(fino: number): StatoCp {
  let s = STATO0;
  for (let g = 0; g <= fino; g++) {
    s = applica(s, mossaIo(g, 0, QUOTA(g), true));
    s = applica(s, mossaRobot(g));
  }
  return s;
}

/** Passo 7: «qualche giro dopo»: i giri 0-3 sono giocati, il tavolo e` prima dell'ultimo (in mano A♦ 9♠ 5♥). */
export const STATO_PRIMA_DELL_ULTIMO: StatoCp = giocaFino(3);

/** Il resoconto della mano, come l'avrebbe mandato il server (breakdown MISURATO col motore vero, KB §2: righe 1/1/1, scoop, nessun foul, nessuna royalty). */
export const RESOCONTO_SCOOP: HandSettledPayload = (() => {
  const finale = giocaFino(4);
  const nessuna = { front: 0, middle: 0, back: 0, total: 0 };
  return {
    handNumber: 1,
    netA: 6,
    netB: -6,
    runningNetA: 6,
    runningNetB: -6,
    handWinner: 'A',
    breakdown: {
      A: { foul: false, scoop: true, lines: { front: 1, middle: 1, back: 1 }, royalties: nessuna },
      B: { foul: false, scoop: false, lines: { front: -1, middle: -1, back: -1 }, royalties: nessuna },
    },
    reveal: { boardA: finale.io, boardB: finale.robot, serverSeed: '', deckOrder: [], discards: { A: [...SCARTI_IO], B: [...SCARTI_ROBOT] } },
    fantasylandNext: { A: false, B: false },
    flCardCountNext: { A: 0, B: 0 },
  };
})();

/** Passo 9: la mano e` chiusa, il resoconto e` sul tavolo. */
export const STATO_RESOCONTO: StatoCp = { ...giocaFino(4), mano: [], resoconto: RESOCONTO_SCOOP };

// ── il passo 8: i perche` delle mosse legali ma non attese (KB §3, le due «altra»; due mosse in piu` trovate misurando) ──
export const ALTRA_FOUL = 'chinesepoker:guida.passi.foul.altra';
export const ALTRA_SENZA_COPPIA = 'chinesepoker:guida.passi.foul.altraSenzaCoppia';
export const ALTRA_STESSO_ESITO = 'chinesepoker:guida.passi.foul.altraStessoEsito';
export const ALTRA_ALTO_FORTE = 'chinesepoker:guida.passi.foul.altraAltoForte';

/**
 * Le sei mosse legali dell'ultimo giro (un posto in mezzo, uno in alto) e il loro esito MISURATO col motore vero (`@qcp/rules` @ 9e799e5, `breakdownPair`):
 *   A♦ in mezzo (con 9♠ o 5♥ in alto): foul, −6 · 9♠ in mezzo, 5♥ in alto: l'attesa, scoop +6 · 9♠ in mezzo, A♦ in alto: scoop +6 (stesso esito) ·
 *   5♥ in mezzo, 9♠ in alto: +1 (mezzo perso) · 5♥ in mezzo, A♦ in alto: foul (l'alto batte il mezzo), −6.
 */
export function altraDelFoul(m: MossaCp): string | undefined {
  const mezzo = m.piazza.find((p) => p.fila === 'middle')?.carta;
  const alto = m.piazza.find((p) => p.fila === 'front')?.carta;
  if (mezzo === undefined || alto === undefined) return undefined;
  if (chiave(mezzo) === chiave(carta('A♦'))) return ALTRA_FOUL;
  if (chiave(mezzo) === chiave(carta('5♥'))) return chiave(alto) === chiave(carta('A♦')) ? ALTRA_ALTO_FORTE : ALTRA_SENZA_COPPIA;
  return ALTRA_STESSO_ESITO;
}

// ── i 12 passi (KB §3) ──────────────────────────────────────────────────────────────────────────────────────────
const fila = (f: Row): BersaglioCp => ({ t: 'fila', fila: f });
const dalla = (c: string): BersaglioCp => ({ t: 'carta', carta: carta(c) });
const CONFERMA: BersaglioCp = { t: 'conferma' };

export const PASSI: readonly Passo<StatoCp, MossaCp, BersaglioCp>[] = [
  { id: 'righe', attesa: AVANTI, evidenzia: [fila('front'), fila('middle'), fila('back')] },
  { id: 'ordine', attesa: AVANTI, evidenzia: [fila('back'), fila('front')] },
  { id: 'prima', attesa: mossaIo(0, 0, 1, false), evidenzia: [dalla('K♠'), fila('back')], mostra: { da: dalla('K♠'), a: fila('back') } },
  { id: 'coppia', attesa: mossaIo(0, 1, 2, false), evidenzia: [dalla('K♥'), fila('back')], mostra: { da: dalla('K♥'), a: fila('back') } },
  {
    id: 'resto',
    attesa: mossaIo(0, 2, 5, true),
    dopo: [mossaRobot(0)],
    evidenzia: [dalla('9♦'), dalla('8♦'), fila('middle'), dalla('3♣'), fila('front'), CONFERMA],
    mostra: { da: dalla('9♦'), a: fila('middle') },
  },
  {
    id: 'pineapple',
    attesa: mossaIo(1, 0, 2, true),
    dopo: [mossaRobot(1)],
    evidenzia: [dalla('Q♦'), dalla('7♦'), fila('middle'), CONFERMA],
    mostra: { da: dalla('Q♦'), a: fila('middle') },
  },
  { id: 'giri', attesa: AVANTI, evidenzia: [fila('back')], salto: STATO_PRIMA_DELL_ULTIMO },
  {
    id: 'foul',
    attesa: mossaIo(4, 0, 2, true),
    dopo: [mossaRobot(4)],
    evidenzia: [dalla('9♠'), dalla('5♥'), fila('middle'), fila('front'), CONFERMA],
    mostra: { da: dalla('9♠'), a: fila('middle') },
    altra: altraDelFoul,
    perche: [ALTRA_FOUL, ALTRA_SENZA_COPPIA, ALTRA_STESSO_ESITO, ALTRA_ALTO_FORTE],
  },
  { id: 'conto', attesa: AVANTI, evidenzia: [{ t: 'totale' }], salto: STATO_RESOCONTO },
  { id: 'bonus', attesa: AVANTI, evidenzia: [] },
  { id: 'bonusAlto', attesa: AVANTI, evidenzia: [] },
  { id: 'fantasyland', attesa: AVANTI, evidenzia: [] },
  { id: 'fantasylandCarte', attesa: AVANTI, evidenzia: [] },
  { id: 'fine', attesa: AVANTI, evidenzia: [] },
];

export const COPIONE_CP: Copione<StatoCp, MossaCp, BersaglioCp> = { stato0: STATO0, passi: PASSI };
